import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { domainForModule, usePermissions } from '../hooks/usePermissions';
import { useEntitlements } from '../hooks/useEntitlements';

interface ProtectedRouteProps {
  moduleId: string;
  children: React.ReactNode;
  /** Allow a module page to render its explicit onboarding/empty state without a condo. */
  allowWithoutCondo?: boolean;
}

export function ProtectedRoute({ moduleId, children, allowWithoutCondo = false }: ProtectedRouteProps) {
  const { user } = useAuth();
  // P2 (2026-10-01, pre-launch override unlock): hasModuleAccess arms the
  // module-toggle override from rawConfigRef, which is populated only after
  // useModuleAccess finishes GET /condos/:id/modules. The guard must hold on
  // that fetch (same pattern as entitlementReady); otherwise on a free-plan
  // condo with an explicit override row the first evaluation sees rawConfigRef
  // === {} , misses the override arm, falls to the entitlement resolver and
  // hard-Navigates to /home/dashboard before the fetch lands — the Navigate
  // unmounts the route, so the later re-render never happens (race observed
  // in browser QA: /modules 200 at 22:29:02.388Z, bounce already rendered).
  const { hasModuleAccess, loading: moduleConfigLoading } = useModuleAccess();
  const { can, loading } = usePermissions();
  // Bug #172 (2026-09-25): entitlements (billing resolver) and permissions load
  // independently. A paid propietario whose permission snapshot lands first used
  // to fall through to hasModuleAccess() while `entitlementReady` was still
  // false — hasModuleAccess fail-closes on a missing entitlement, so the guard
  // rendered the page for ~400ms and then hard-bounced to /home/dashboard
  // (which usuariosOnly owners immediately redirect to /home/staff-directory).
  // Hold the loading state until the entitlement cache resolves, then apply the
  // real deny. Never substitute a 403 from the legacy /condos/:id/modules
  // module-config call: useModuleAccess already degrades that fetch to
  // all-enabled and hasModuleAccess is entitlement-resolver-backed only.
  const { entitlementReady } = useEntitlements();

  // Do not turn a missing condo assignment into a redirect loop/blank page for
  // modules that provide their own safe onboarding state. Assigned users still
  // go through the normal module permission check below.
  if (allowWithoutCondo && !user?.condoId) {
    return children;
  }
  
  if (loading || !entitlementReady || moduleConfigLoading) return <div className="min-h-[12rem] flex items-center justify-center text-slate-500">Cargando permisos…</div>;
  if (!can(domainForModule(moduleId))) return <div className="min-h-[12rem] flex items-center justify-center p-8 text-center"><p className="text-lg font-semibold text-slate-600">No tienes permiso para ver este módulo</p></div>;
  if (!hasModuleAccess(moduleId)) {
    return <Navigate to="/home/dashboard" replace />;
  }
  
  return children;
}

export default ProtectedRoute;
